import { useAuth } from "@/context/AuthContext";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users2,
  Ticket,
  UserPlus,
  ShieldCheck,
  LogOut,
  Building2,
  FileSignature,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "sales", "technician"] },
  { to: "/customers", label: "Customers", icon: Users2, roles: ["admin", "sales", "technician"] },
  { to: "/customers/new", label: "New Customer", icon: UserPlus, roles: ["admin", "sales"] },
  { to: "/tickets", label: "Tickets", icon: Ticket, roles: ["admin", "sales", "technician"] },
  { to: "/settings", label: "Agreement & E-Sign", icon: FileSignature, roles: ["admin"] },
  { to: "/users", label: "Team & Access", icon: ShieldCheck, roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Administrator", sales: "Sales Department", technician: "Technician Department" };

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex flex-col">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <div>
              <div className="font-display text-lg leading-tight text-primary">My Geeks LLC</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Customer Relations
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border transition-colors duration-150 ${
                    isActive
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-transparent text-foreground hover:bg-secondary"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="mb-3">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono">
              {ROLE_LABEL[user?.role]}
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            size="sm"
            className="w-full rounded-none justify-start"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-6 sticky top-0 z-50">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {ROLE_LABEL[user?.role]} · Workspace
          </div>
          <div className="text-xs font-mono text-muted-foreground">EST. NEW YORK</div>
        </header>
        <main className="flex-1 p-6 md:p-8 overflow-x-auto">{children}</main>
      </div>
    </div>
  );
}
